import summaryStore from "@/store/summary.store";
import summaryChapterStore from "@/store/summaryChapter.store";
import summaryCommonStore from "@/store/summaryCommon.store";
import summarySpeakerStore from "@/store/summarySpeaker.store";
import summarySrcStore from "@/store/summarySrc.store";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import usePollingSummary from "./usePollingSummary";
import { DetailErrorType } from "@/constants/record";
import { Id, toast } from "react-toastify";
import { listenOnline } from "@/utils/networkHelper";

export enum EFetchStatus {
  LOADING,
  SUCCESS,
  ERROR,
}

let toastId: Id;
const useFetchSummary = (streamId?: string, fromShare = false) => {
  const pollingSrcRef = useRef<NodeJS.Timeout>();
  const pollingChapterInfoRef = useRef<NodeJS.Timeout>();
  const pollingSpeakerSummaryRef = useRef<NodeJS.Timeout>();

  const abortControllerRef = useRef(new AbortController());
  const navigate = useNavigate();

  const commonInitParam = streamId
    ? {
      streamName: `live/${streamId}`,
      reload: false,
      contentStep: "SRC_DOC",
      cover: true,
    }
    : null;

  const { pollingSummary, pollingSummaryRef } = usePollingSummary(commonInitParam);
  const { fetchDetailInfo, generateTitle } = summarySrcStore.getState();
  const { setFullLoading, setErrorStatus, fetchAudioInfo } =
    summaryCommonStore.getState();
  const retryStatus = summaryCommonStore((state) => state.retryStatus);
  const shareInfo = summaryCommonStore((state) => state.shareInfo);

  const summary = summaryStore((state) => state.summary);

  const { setChapterLoading, fetchChapterInfo } =
    summaryChapterStore.getState();

  const { fetchSpeakerSummary, setSpeakerLoading } =
    summarySpeakerStore.getState();
  const loading = !summary?.summaryFinish || retryStatus === 1;

  // 轮询获取会议原文
  const pollingSrc = async (streamId: string) => {
    if (!streamId) return;
    try {
      const res = await fetchDetailInfo(
        {
          recordingType: "live",
          streamId,
          fromShare: fromShare
        },
        true,
        {
          signal: abortControllerRef.current.signal,
        }
      );
      console.log("pollingSrc res = ", res);
      if (res && !res.completeDocFinish) {
        pollingSrcRef.current = setTimeout(() => {
          pollingSrc(streamId);
        }, 3000);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      // 当没有权限时，跳转到详情权限异常页
      if (err?.errCode === 40002104) {
        navigate(`/SummaryPermissionError?streamId=${streamId}`, {
          replace: true,
        });
      } else if (err?.errCode) {
        console.log("pollingSrc error = ", err);
        // 录制信息已删除
        if (err.errCode === DetailErrorType.deleted) {
          setErrorStatus(DetailErrorType.deleted);
          return;
        }
        if (err.errCode === DetailErrorType.ERR_NETWORK) {
          toastId && toast.dismiss(toastId);
          console.log("监听网络恢复");
          let count = 1;
          const unListenOnlineCb = listenOnline({
            offlineCb: () => {
              if (count < 10) {
                count += 1;
              } else {
                unListenOnlineCb();
                summaryCommonStore.getState().setErrorStatus(DetailErrorType.ERR_NETWORK);
                toastId && toast.dismiss(toastId);
              }
            },
            onlineCb: () => {
              console.log("网络恢复");
              toastId && toast.dismiss(toastId);
              toastId = toast.success(`网络已恢复`);
              summaryCommonStore.getState().refreshSummaryKey();
            },
            timeout: 3000
          });
          toastId = toast.error(`网络异常，请恢复后重试`, {
            autoClose: false,
          });
          return;
        } else {
          setErrorStatus(err.errCode);
        }
      }
    }
  };


  // 轮询获取智能章节
  const pollingChapterInfo = async (streamId: string) => {
    if (!commonInitParam) return;
    setChapterLoading(true);
    const res = await fetchChapterInfo(commonInitParam, false, {
      signal: abortControllerRef.current.signal,
    });

    if (res && !res.summaryFinish) {
      pollingChapterInfoRef.current = setTimeout(() => {
        pollingChapterInfo(streamId);
      }, 3000);
    } else if (res && res.summaryFinish) {
      setChapterLoading(false);
    }
  };

  // 轮询获取发言人总结
  const pollingSpeakerSummary = async (streamId: string) => {
    if (!commonInitParam) return;
    setSpeakerLoading(true);
    const res = await fetchSpeakerSummary(commonInitParam, false, {
      signal: abortControllerRef.current.signal,
    });

    if (res && !res.summaryFinish) {
      pollingSpeakerSummaryRef.current = setTimeout(() => {
        pollingSpeakerSummary(streamId);
      }, 3000);
    } else if (res && res.summaryFinish) {
      setSpeakerLoading(false);
    }
  };

  useEffect(() => {
    if (!streamId) return;
    setFullLoading(false);
    pollingSrc(streamId);
    summaryChapterStore.getState().fetchKeyframe(`live/${streamId}`);
    generateTitle(false, `live/${streamId}`);
    console.log("shareInfo = ", shareInfo);
    if (fromShare) {
      pollingSummary();
    }
    pollingChapterInfo(streamId);
    pollingSpeakerSummary(streamId);
    fetchAudioInfo(
      {
        streamName: `live/${streamId}`,
      },
      { signal: abortControllerRef.current.signal }
    );
    const pollingSummaryRefCurrent = pollingSummaryRef.current;
    return () => {
      summaryCommonStore.getState().resetAudioInfo();
      pollingSrcRef.current && clearTimeout(pollingSrcRef.current);
      pollingSummaryRefCurrent && clearTimeout(pollingSummaryRefCurrent);
      pollingChapterInfoRef.current && clearTimeout(pollingChapterInfoRef.current);
      pollingSpeakerSummaryRef.current && clearTimeout(pollingSpeakerSummaryRef.current);
    };
  }, [streamId, shareInfo]);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    return () => {
      pollingSrcRef.current && clearTimeout(pollingSrcRef.current);
      pollingChapterInfoRef.current && clearTimeout(pollingChapterInfoRef.current);
      pollingSpeakerSummaryRef.current && clearTimeout(pollingSpeakerSummaryRef.current);
      abortControllerRef.current.abort();
    };
  }, []);

  return { loading };
};

export default useFetchSummary;
